import {
  NOTIFICATION_KIND_LABELS,
  type NotificationDeliveryItem,
  notificationRecipientEmailSchema,
  type ResendNotificationInput,
} from '@repo/api-contracts';
import { type ReactElement, useId, useState } from 'react';
import { formStyles, Input, Label, OrderActionDialog } from '@/shared/ui';

interface ResendNotificationDialogProps {
  readonly delivery: Pick<
    NotificationDeliveryItem,
    'id' | 'kind' | 'recipientEmail' | 'recipientRedacted'
  >;
  readonly onSubmit: (input: ResendNotificationInput) => Promise<unknown>;
  readonly onClose: () => void;
}

const EMPTY_MESSAGE = '수신 이메일을 입력해 주세요.';
const INVALID_MESSAGE = '올바른 이메일 주소를 입력해 주세요.';
/** 보낸 뒤 대화상자에 남기는 완료 안내 — 원본 행은 그대로 남아 [재발송]이 다시 보이므로, 결과는 새 기록에서 보라고 알린다. */
const COMPLETE_MESSAGE = "재발송을 요청했습니다. 새 '대기' 기록에서 결과를 확인하세요.";
/** OrderActionDialog 는 주문 상태 충돌(ORDER_STATE_CONFLICT) 때만 다시 읽기를 부른다. 알림 재발송 거절에는 다시 읽을 상세가 없다. */
const noReload = (): void => undefined;

/**
 * 알림 재발송(알림 설계 §5-3·N7). 원본과 같은 종류·대상으로 새 발송 기록을 만든다. 수신 주소는 원본 주소로 채워 열고,
 * 반송된 주소를 고칠 수 있게 바꿔 넣을 수 있다. 다듬은 주소가 원본과 같으면 주소 없이 요청한다. 그러면 서버가 원본 주소를 쓰고
 * 감사의 recipientChanged 는 false 다. 주소 칸은 type="text" 다. type="email" 이면 브라우저 기본 검증이 안내 문구보다 먼저 막는다.
 * 보내면 바로 닫지 않고 완료 안내를 보이며 [보내기]를 막는다 — 같은 원본으로 두 번 보내 고객에게 메일이 두 번 가지 않게 한다.
 */
export function ResendNotificationDialog({
  delivery,
  onSubmit,
  onClose,
}: ResendNotificationDialogProps): ReactElement {
  const id = useId();
  const [email, setEmail] = useState(delivery.recipientEmail ?? '');

  return (
    <OrderActionDialog
      title="알림 재발송"
      submitLabel="보내기"
      onReload={noReload}
      onClose={onClose}
      validate={() => {
        if (email.trim() === '') return EMPTY_MESSAGE;
        return notificationRecipientEmailSchema.safeParse(email).success ? null : INVALID_MESSAGE;
      }}
      onSubmit={async () => {
        const parsed = notificationRecipientEmailSchema.safeParse(email);
        if (!parsed.success) throw new Error(INVALID_MESSAGE);
        await onSubmit(
          parsed.data === delivery.recipientEmail
            ? { deliveryId: delivery.id }
            : { deliveryId: delivery.id, recipientEmail: parsed.data },
        );
        return COMPLETE_MESSAGE;
      }}
    >
      <p className={formStyles.hint}>
        {NOTIFICATION_KIND_LABELS[delivery.kind]} 알림을 새 기록으로 다시 보냅니다. 꺼 둔 종류여도
        보내지만, 견적·주문 상태가 바뀌어 보낼 수 없으면 건너뜁니다.
      </p>
      {delivery.recipientRedacted ? (
        <p className={formStyles.notice}>
          탈퇴 회원이라 원래 주소가 지워졌습니다. 보낼 주소를 입력해 주세요.
        </p>
      ) : null}
      <div className={formStyles.field}>
        <Label htmlFor={`${id}-email`}>수신 이메일</Label>
        <Input
          id={`${id}-email`}
          type="text"
          inputMode="email"
          autoComplete="off"
          spellCheck={false}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </div>
    </OrderActionDialog>
  );
}
